import {
    faBan,
    faEyeSlash,
    faFileInvoice,
    faPersonChalkboard,
    faPlay,
    faPlus,
    faSync
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import AttendanceBadge from '../Attendance/AttendanceBadge';
import { CompactAllocation } from '../types';

interface Props {
    allocation: CompactAllocation;
}

// Compact summary of a booking in the list: id, space, title and the timestamps around it.
const AllocationMeta = ({ allocation }: Props) => {
    const { t } = useTranslation('space-manager');

    return (
        <div className="tw-my-2 tw-flex tw-w-full tw-flex-col tw-gap-1 tw-pr-4 tw-text-xs tw-text-accents-6">
            {allocation.is_shadow && (
                <span className="tw-flex tw-min-w-fit tw-max-w-full tw-items-center tw-gap-2 tw-text-warning">
                    <FontAwesomeIcon icon={faEyeSlash} size="xs" />
                    <span>{t('allocation.form.shadowLabel')}</span>
                </span>
            )}
            {/* renders nothing unless the VerifyAttendance effect applies to this booking */}
            {!!allocation.attendance && (
                <span className="tw-flex tw-min-w-fit tw-max-w-full">
                    <AttendanceBadge attendance={allocation.attendance} />
                </span>
            )}
            <span className="tw-flex tw-min-w-fit tw-max-w-full tw-items-center tw-gap-2 tw-text-lg">
                # {allocation.id} | {allocation.space_name}
            </span>
            {allocation.title && (
                <span className="tw-flex tw-min-w-fit tw-max-w-full tw-items-center tw-gap-2">{allocation.title}</span>
            )}
            {allocation.cancellation_reason && (
                <span className="tw-flex tw-min-w-fit tw-max-w-full tw-items-baseline tw-gap-2 tw-whitespace-normal tw-text-red-500">
                    <FontAwesomeIcon icon={faBan} size="xs" />
                    <span>{allocation.cancellation_reason}</span>
                </span>
            )}
            <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-border-0 tw-border-t tw-border-solid tw-border-accents-3 tw-pt-2">
                <span className="tw-flex tw-min-w-fit tw-max-w-full tw-items-center tw-gap-2">
                    <FontAwesomeIcon icon={faPlay} size="xs" />
                    <span>{`${moment(allocation.start).format('DD.MM.YY HH:mm')} - ${moment(allocation.end).format('HH:mm')}`}</span>
                </span>
                <span className="tw-flex tw-min-w-fit tw-max-w-full tw-items-center tw-gap-2">
                    <FontAwesomeIcon icon={faPlus} size="xs" />
                    <span>{moment(allocation.created_at).format('DD.MM.YY HH:mm')}</span>
                </span>
                {allocation.created_at !== allocation.updated_at && (
                    <span className="tw-flex tw-min-w-fit tw-max-w-full tw-items-center tw-gap-2">
                        <FontAwesomeIcon icon={faSync} size="xs" />
                        <span>{moment(allocation.updated_at).format('DD.MM.YY HH:mm')}</span>
                    </span>
                )}
                {allocation.cancelled_at && (
                    <span className="tw-flex tw-min-w-fit tw-max-w-full tw-items-center tw-gap-2 tw-text-red-500">
                        <FontAwesomeIcon icon={faBan} size="xs" />
                        <span>{moment(allocation.cancelled_at).format('DD.MM.YY HH:mm')}</span>
                    </span>
                )}
                {allocation.invoices?.length > 0 && (
                    <span className="tw-flex tw-min-w-fit tw-max-w-full tw-items-center tw-gap-2">
                        <FontAwesomeIcon icon={faFileInvoice} size="xs" />
                        <span>{moment(allocation.invoices?.[0]?.created_at).format('DD.MM.YY HH:mm')}</span>
                    </span>
                )}
                {allocation.trainer && (
                    <span className="tw-flex tw-min-w-fit tw-max-w-full tw-items-center tw-gap-2">
                        <FontAwesomeIcon icon={faPersonChalkboard} size="xs" />
                        <span>{allocation.trainer.name}</span>
                    </span>
                )}
            </div>
        </div>
    );
};

export default AllocationMeta;
